import { CustomVariableCategory } from '@/types/custom-variables'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<CustomVariableCategory>({
  pathCategory: 'custom_variable_categories',
})

export function useGetCustomVariableCategory({
  id,
  ...options
}: UseGetDetailOptions<CustomVariableCategory>) {
  const query = useGetDetail<CustomVariableCategory>({
    id,
    queryKey: queryKeys.customVariableCategories.detail({
      id,
      filters: options.filters,
      persist: true,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    customVariableCategory: query.data,
    ...query,
  }
}

export function useCreateCustomVariableCategory(
  options: UseCreateDetailOptions<CustomVariableCategory> = {},
) {
  const mutation = useCreateDetail<CustomVariableCategory>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createCustomVariableCategory: mutation.mutate,
    createCustomVariableCategoryAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateCustomVariableCategory(
  options: UseUpdateDetailOptions<CustomVariableCategory> = {},
) {
  const mutation = useUpdateDetail<CustomVariableCategory>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateCustomVariableCategory: mutation.mutate,
    updateCustomVariableCategoryAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteCustomVariableCategory(
  options: UseDeleteDetailOptions = {},
) {
  const mutation = useDeleteDetail<CustomVariableCategory>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteCustomVariableCategory: mutation.mutate,
    deleteCustomVariableCategoryAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetCustomVariableCategories({
  filters,
  ...options
}: UseGetListOptions<CustomVariableCategory> = {}) {
  const query = useGetList<CustomVariableCategory>({
    filters,
    queryKey: queryKeys.customVariableCategories.list({
      filters,
      persist: true,
    }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    customVariableCategories: query.allData,
    ...query,
  }
}

export function useUpdateCustomVariableCategories(
  options: UseUpdateListOptions<CustomVariableCategory> = {},
) {
  const mutation = useUpdateList<CustomVariableCategory>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateCustomVariableCategories: mutation.mutate,
    updateCustomVariableCategoriesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteCustomVariableCategories({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<CustomVariableCategory>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteCustomVariableCategories: mutation.mutate,
    deleteCustomVariableCategoriesAsync: mutation.mutateAsync,
    ...mutation,
  }
}
